/**
 * @author yuanzeyu
 * @date 2020/10/14
 * @desc credential sdk.
 */

import { v4 as uuidv4 } from 'uuid';
import { Jwt, WalletVcItem } from './types';

/**
 * get credential json from base64 jwt string.
 * @param jwtStr :jwt string
 */
export const jwt2Json: <T>(jwtStr: string) => Jwt<T> = (jwtStr: string) => JSON.parse(atob(jwtStr.split('.')[1]));

/**
 * build header object of jwt
 * @param publicKeyId
 */
export const getJwtHeader = (publicKeyId: string) => ({
  alg: 'ES256',
  kid: publicKeyId,
  typ: 'JWT',
});

/**
 * build wallet jwt payload
 * @param ontId
 * @param addressList
 */
export const getWalletJwtPayload = (ontId: string, addressList: WalletVcItem[]) => {
  const now = new Date();
  const stamp = Math.floor(now.getTime() / 1000);
  return { // json keys sort by Initials for java sdk.
    iss: ontId, // jwt signer
    exp: stamp + 86400, // expire time
    nbf: stamp, // disable before this time
    iat: stamp, // jwt sign time
    jti: `urn:uuid:${uuidv4()}`, // unique id
    vc: {
      '@context': [
        'https://www.w3.org/2018/credentials/v1',
        'https://ontid.ont.io/credentials/v1',
        'claim:onto_oscore_authentication', // declare wallet address vc
      ],
      type: [
        'VerifiableCredential',
        'RelationshipCredential',
      ],
      credentialSubject: addressList,
      credentialStatus: { // contract hash
        id: '4f7f159ac4b9913bb185fdf1895705f61b7d0cc6',
        type: 'AttestContract',
      },
      proof: {
        created: now.toISOString(),
        proofPurpose: 'assertionMethod',
      },
    },
  };
};

/**
 * object to base64 string
 * @param obj
 */
export const encodeBase64 = (obj: Object) => btoa(JSON.stringify(obj));

/**
 * jwt string without sign
 * @param ontId
 * @param pubKeyId
 * @param addressList
 */
export const getPreJwtString = (ontId: string, pubKeyId: string, addressList: WalletVcItem[]) => {
  const header = getJwtHeader(pubKeyId);
  const payload = getWalletJwtPayload(ontId, addressList);
  return `${encodeBase64(header)}.${encodeBase64(payload)}`;
};

/**
 * get wallet address list jwt base64 string
 */
export const getWalletJwtString = (preVc:string, sign:string = '') => `${preVc}.${btoa(sign)}`;
